import Facade from '@/Facade'
import { Model } from '@/model/Model'
import GameSignals from '@/signals/GameSignals'
import { toMajorUnits } from '@/utils/CurrencyUnits'
import { getFreeSpinsFinalWinAmount, isHoldAndWinInsideFreeSpins } from '@/states/freespins/FreeSpinsFinalWin'
import { capFeatureWinAmount } from '@/states/round/MaxWinCap'

export const isEndingHoldAndWinRound = (model: Model): boolean => {
    const spinType = String(model.spinData?.spin_type || '').toLowerCase()
    const nextRoundType = String(model.spinData?.next_round_type || '').toLowerCase()
    const respinsLeft = Number(model.spinData?.respins_left ?? 0)
    const hasHoldAndWinContext =
        Boolean(model.spinState.isHoldAndWinActive) ||
        Boolean(model.isBonusGameNow) ||
        model.spinData?.hold_and_win_extra != null
    const hasRespinsLeft =
        (nextRoundType === 'respins' || nextRoundType === 'respin') &&
        Number.isFinite(respinsLeft) &&
        respinsLeft > 0

    return hasHoldAndWinContext && spinType === 'respin' && !hasRespinsLeft
}

//## Respins has different total-win.
//# Normal Case:
//   - Base Game: respin_info.total_win_amount
//   - Within freespin: respin_info.total_win_amount
//# Corner case: (Fixed)
//   - Last freespin triggered H&w : respin_info.total_win_amount
const readRespinInfoTotalWin = (model: Model): number | null => {
    const currentRound = (model.spinData as any)?.current_round
    const extra = currentRound?.other_feature_win_details || currentRound?.extra
    const total = Number(extra?.respin_info?.total_win_amount)

    return Number.isFinite(total) && total > 0 ? total : null
}

/** H&W feature win in minor units — server respin_info total first, then round-level fallbacks. */
export const getHoldAndWinFinalWinAmount = (model: Model): number => {
    const spinData = model.spinData as any
    const currentRound = spinData?.current_round
    const candidates = [
        readRespinInfoTotalWin(model),
        spinData?.win_currency,
        spinData?.win_coin,
        currentRound?.win_amount_currency,
        currentRound?.win_currency,
        currentRound?.win_amount,
        model.winCounter?.totalWin,
    ]

    for (const candidate of candidates) {
        const value = Number(candidate)
        if (Number.isFinite(value) && value > 0) {
            return capFeatureWinAmount(model, value)
        }
    }

    return 0
}

/**
 * True when this H&W ended on the last free spin, so FinalState rewalks into the FS
 * summarized commendation and FreeSpinsSummaryState. Mirrors
 * HoldAndWinSummaryState.shouldRouteToFreeSpinsSummary.
 */
const willRouteToFreeSpinsSummary = (model: Model): boolean => {
    const freebetAmountLeft = Number(model.spinData?.freebet?.amount_left ?? -1)
    return isHoldAndWinInsideFreeSpins(model) && Number.isFinite(freebetAmountLeft) && freebetAmountLeft <= 0
}

/**
 * Credit the H&W win into the Win Meter once per feature end (total-win popup).
 * Idempotent if called more than once.
 */
export const commitHoldAndWinWinToMeter = (model: Model): void => {
    if ((Facade as any).holdAndWinWinMeterCommitted) {
        return
    }

    const holdAndWinWin = getHoldAndWinFinalWinAmount(model)
    if (!(holdAndWinWin > 0)) {
        return
    }

    ;(Facade as any).holdAndWinWinMeterCommitted = true

    if (isHoldAndWinInsideFreeSpins(model)) {
        // FS meter accumulates via updateWinPanelValue — add H&W, then snap up to server FS total.
        GameSignals.updateWinPanelValue.emit(toMajorUnits(holdAndWinWin))

        //## Re-Validating the Win Amount at last spin of FS 
        if (willRouteToFreeSpinsSummary(model)) {
            return
        }

        const fsTotal = getFreeSpinsFinalWinAmount(model)
        if (fsTotal > 0) {
            ;(GameSignals as any).setWinPanelValueAtLeast?.emit(toMajorUnits(fsTotal))
        }
        return
    }

    ;(GameSignals as any).setWinPanelValue?.emit(toMajorUnits(holdAndWinWin))
}
